<template>
  <v-layout class="email-create-container">
    <v-layout wrap class="form-wrapper">
      <v-layout wrap>
        <v-flex xs12>
          <div v-if="$apollo.loading">
            <spinner />
          </div>
          <form v-scroll-to-error @submit.prevent="submitForm">
            <v-flex class="title-screen">
              <span>{{ adminText('workflowsAll.create_e_mail_b558083', 'Create e-mail') }}</span>
            </v-flex>
            <v-flex x4>
              <div class="title-description screen-description" />
            </v-flex>
            <v-flex>
              <create-theater-event-email-form
                ref="formRef"
                :initial-values="form"
                :event-template-id="eventTemplateId"
                :organization-slug="organizationSlug"
                :initially-selected-event-id="initiallySelectedEventId"
                is-performance-selecting-enabled
                ticket-filter-enabled
                :vs="$v"
                @values="form = $event"
              />
            </v-flex>
            <div class="email-shortcodes">
              <email-shortcodes :shortcodes="shortcodes" @shortcode="shortcodeAdded" />
            </div>
            <div class="buttons-container">
              <main-button flat @click.prevent="goBack"> {{ adminText('workflowsAll.cancel_1507c40', 'CANCEL') }} </main-button>
              <div class="lower-buttons">
                <main-button flat outline @click="sendTestEmail"> {{ adminText('workflowsAll.send_test_email_0d478c0', 'SEND TEST EMAIL') }} </main-button>
                <main-button depressed @click="$emit('submit')"> {{ adminText('workflowsAll.send_a2469c4', 'SEND') }} </main-button>
              </div>
            </div>
          </form>
        </v-flex>
      </v-layout>
    </v-layout>
    <div class="email-shortcodes desktop-shortcodes">
      <email-shortcodes :shortcodes="shortcodes" @shortcode="shortcodeAdded" />
    </div>
    <success-snackbar
      :open.sync="wasTestEmailSent"
      :title="adminText('workflowsAll.success_42a8f65', 'Success')"
      :message="adminText('workflowsAll.your_test_email_was_sent_successfully_to_the__96f4428', 'Your test email was sent successfully to the organizer!')"
    />
    <error-snackbar
      :open="emailError"
      :title="adminText('workflowsAll.oops_c8e633f', 'Oops')"
      :message="adminText('workflowsAll.an_error_occurred_while_trying_to_send_your_e_adbb9de', 'An error occurred while trying to send your email.')"
    />
  </v-layout>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { required, email } from 'vuelidate/lib/validators'
import EmailShortcodes from './email-shortcodes'
import SuccessSnackbar from '../shared/snackbars/success-snackbar'
import ErrorSnackbar from '../shared/snackbars/errors-snackbar'
import { CREATE_EMAIL, SEND_EMAIL } from '../../graphql/email.graphql'
import { submitGuard } from '../../utils'
import { GET_ORGANIZATION } from '../../graphql/organization.graphql'
import CreateTheaterEventEmailForm from '@/components/emails/create-theater-event-email-form.vue'

export default {
  mixins: [WorkflowDisplay],
  name: 'theater-email-create-container',
  components: {
    CreateTheaterEventEmailForm,
    EmailShortcodes,
    SuccessSnackbar,
    ErrorSnackbar,
  },
  props: {
    organizationSlug: String,
    eventTemplateId: String,
    initialValues: {
      type: Array,
      default: () => [],
    },
    initiallySelectedEventId: String,
  },
  data() {
    return {
      form: { content: '', replyTo: '' },
      wasTestEmailSent: false,
      emailError: false,
      shortcodes: [
        'Event name',
        'Attendee name',
        'Event location',
        'Attendee QR code link',
        'Attendee QR code image',
        'Event start',
        'Attendee email',
        'Event end',
        'Country',
        'Function',
        'Event type',
      ],
    }
  },
  validations() {
    return {
      form: {
        replyTo: { required, email },
        recipients: { required },
        subject: { required },
        content: { required },
      },
    }
  },
  created() {
    if (this.initialValues.length) {
      this.form.recipients = 'Specific attendees'
      this.form.attendees = this.initialValues.map(value => value.toString())
    }
  },
  apollo: {
    getOrganization: {
      query: GET_ORGANIZATION,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
        }
      },
      result({ data, loading }) {
        if (!loading) {
          const {
            getOrganization: { email },
          } = data
          this.form.replyTo = email
        }
      },
    },
  },
  methods: {
    async shortcodeAdded(shortcode) {
      if (this.form.content) {
        this.form.content = this.form.content
          .slice(0, this.form.content.length - 4)
          .concat(` { ${shortcode} }`)
      } else {
        this.form.content = this.form.content.concat(`{ ${shortcode} }`)
      }
    },
    async createNewEmail() {
      const { replyTo, recipients, subject, content, attendees, categories, orderItemIds } = this.form
      const attendeeIdentifiers = this.normalizeIdentifiers(attendees)
      const categoryIdentifiers = this.normalizeIdentifiers(categories)
      const selectedOrderItemIds = this.normalizeIdentifiers(orderItemIds)
      const identifiers = attendeeIdentifiers.length ? attendeeIdentifiers : categoryIdentifiers
      const email = await this.$apollo.mutate({
        mutation: CREATE_EMAIL,
        variables: {
          eventId: this.$refs.formRef.$data.selectedEventId,
          email: {
            replyTo: replyTo,
            recipientType: recipients,
            subject: subject,
            content: content,
            identifiers,
            meta: {
              orderItemIds: selectedOrderItemIds,
            },
          },
        },
      })
      const {
        data: {
          createEmail: { id },
        },
      } = email
      const response = await this.$apollo.mutate({
        mutation: SEND_EMAIL,
        variables: {
          emailId: id,
        },
      })
      const {
        data: { sendEmail },
      } = response
      if (!sendEmail) {
        this.emailError = true
      } else {
        this.goTo(this.routes.theaterOrganizer.emailsTheater, {
          wasEmailSent: true,
          initiallySelectedEventId: this.$refs.formRef.$data.selectedEventId,
        })
      }
    },
    async sendTestEmail() {
      const { replyTo, subject, content } = this.form
      const {
        data: {
          getOrganization: { email: organizerEmail },
        },
      } = await this.$apollo.query({
        query: GET_ORGANIZATION,
        variables: {
          organizationSlug: this.organizationSlug,
        },
      })
      const testEmailData = {
        replyTo,
        content,
        subject,
        organizerEmail,
        eventId: this.$refs.formRef.$data.selectedEventId,
      }

      const response = await this.$apollo.mutate({
        mutation: SEND_EMAIL,
        variables: {
          testEmailData: testEmailData,
        },
      })
      if (response) {
        this.wasTestEmailSent = true
      } else {
        this.emailError = true
      }
    },
    async submitForm() {
      await submitGuard(this, async () => {
        await this.createNewEmail()
      })
    },
    normalizeIdentifiers(identifiers = []) {
      return identifiers
        .map(identifier => {
          if (identifier && typeof identifier === 'object') {
            return identifier.id
          }

          return identifier
        })
        .filter(identifier => identifier !== null && identifier !== undefined && identifier !== '')
        .map(identifier => identifier.toString())
    },
  },
}
</script>

<style scoped>
.screen-description {
  max-width: 680px;
  margin-bottom: 28px;
}
.form-container {
  margin-right: 30px;
  max-width: 40.75rem;
}
.desktop-shortcodes {
  margin: 352px -60px 0 0;
  min-width: 200px !important;
  display: block !important;
  margin-left: 24px;
}
.email-shortcodes {
  display: none;
}
@media screen and (max-width: 1279px) {
  .desktop-shortcodes {
    display: none !important;
  }
  .email-shortcodes {
    margin: 16px 0;
    margin-bottom: 32px;
    display: block;
  }
}
.title-screen {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.5rem;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
.form-wrapper {
  max-width: 40.75rem;
}

@media screen and (max-width: 800px) {
  .title-screen {
    display: none;
  }
  .form-wrapper {
    margin-top: 24px;
  }
}
</style>
